<template>
  <div class="vz-pagination-new">
    <div class="vz-pagination-new-limit">
      <span class="vz-pagination-new-limit-title">
          На странице
      </span>

      <vz-select
        v-model="selectedLimit"
        :items="selectItems"
        @change="onChangeLimit"
      />
    </div>
    <template v-if="!publicStyle">
      <div
        v-if="!edit"
        class="vz-pagination-new-pages"
        @click="startEdit"
      >
        <div>
          {{ pagesText }}
        </div>
      </div>
      <vz-input
        v-else
        v-model="pageInput"
        type="number"
        ref="editPageInput"
        class="vz-pagination-new-pages-input"
        @blur="stopEdit"
        @keyup.enter="onEnterDown"
      />

      <div
        class="vz-pagination-new-nav-page start"
        :class="{ disabled: isDisabledFirst }"
        @click="onBorderPaginate(true)"
      />
      <div
        class="vz-pagination-new-nav-page prev"
        :class="{ disabled: isDisabledFirst }"
        @click="onPaginate(true)"
      />
      <div
        class="vz-pagination-new-nav-page next"
        :class="{ disabled: isDisabledLast }"
        @click="onPaginate(false)"
      />
      <div
        class="vz-pagination-new-nav-page end"
        :class="{ disabled: isDisabledLast }"
        @click="onBorderPaginate(false)"
      />
    </template>
    <div v-else class="public-style">
      <div
        class="vz-pagination-new-nav-page prev"
        :class="{ disabled: isDisabledFirst }"
        @click="onPaginate(true)"
      />
      <vz-button
        v-for="pageNumber of pageList"
        class="public-style-pagination-button"
        :disabled="!pageNumber"
        :variant="pageNumber === modelValue.currentPage ? 'primary' : ''"
        @click="setPage(pageNumber)"
      >
        {{ pageNumber || '...' }}
      </vz-button>
      <div
        class="vz-pagination-new-nav-page next"
        :class="{ disabled: isDisabledLast }"
        @click="onPaginate(false)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ComponentPublicInstance, PropType } from 'vue'
import { Pagination, PaginationItem } from '~/types/types'
import VzInput from '~/components/ui-kit/vz-input.vue'
import { useWindowSize } from '@vueuse/core'
import VzSelect from '~/components/ui-kit/vz-select.vue';

const emit = defineEmits(['update:modelValue', 'change', 'change-limit'])
const route = useRoute()
const router = useRouter()
const { width } = useWindowSize()
const props = defineProps({
  modelValue: {
    type: Object as PropType<Pagination>,
    default: () => ({
      limit: 10,
      pages: 1,
      currentPage: 1
    })
  },
  limits: {
    type: Array as PropType<Number[]>,
    default: () => [5, 10, 15]
  },
  useParams: {
    type: Boolean,
    default: false
  },
  publicStyle: {
    type: Boolean,
    default: false
  },
})

const editPageInput = ref<ComponentPublicInstance<typeof VzInput>|null>(null)
const selectedLimit = ref(props.modelValue.limit?.toString() || '10')
const pageInput = ref(0)
const edit = ref(false)

onMounted(() => {
    pageInput.value = page.value
})

onBeforeMount((): void => {
    selectedLimit.value = (props.limits.find(pl => +pl === +paginator.value.limit) || props.limits[0]).toString()
    if (process.client && props.useParams && JSON.stringify(paginator.value) !== JSON.stringify(props.modelValue)) {
      setPaginator(paginator.value)
    }
})

const visiblePageLength = computed(() => {
  return width.value < 600 ? 1 : 2
})

const pageList = computed(() => {
  const result: Array<number|null> = []

  if (!props.modelValue) {
    return result
  }

  for (let i = 1; i < props.modelValue.pages + 1; i++) {
    if (i === 1 || i === props.modelValue.pages) {
      result.push(i)
      continue;
    }
    if (Math.abs(i - props.modelValue.currentPage) < visiblePageLength.value) {
      result.push(i)
      continue;
    }
    if (result[result.length - 1] !== null) {
      result.push(null)
    }
  }

  return result
})

const page = computed({
    get: (): number => {
        return paginator.value.currentPage || 1
    },
    set: (value: number) => {
        if (value == paginator.value.currentPage) {
            return
        }
        setPaginator({
            ...paginator.value,
            currentPage: value
        })
    }
})

const paginator = computed((): Pagination => {
    if (!props.useParams) {
        return props.modelValue
    } else {
        return {
            limit: +(route.query.limit || props.modelValue.limit),
            pages: props.modelValue.pages,
            currentPage: +(route.query.page || props.modelValue.currentPage)
        }
    }
})

const selectItems = computed((): PaginationItem[] => {
    return props.limits.map((ar) => {
        return { title: ar.toString(), value: ar.toString() }
    })
})

const pagesText = computed((): string => {
    const separator = width.value < 400 ? '/' : ' из '
    return paginator.value.currentPage + separator + paginator.value.pages
})

const isDisabledFirst = computed((): boolean => {
    return paginator.value.currentPage === 1
})

const isDisabledLast = computed((): boolean => {
    return paginator.value.currentPage === paginator.value.pages
})

const mask = computed((): string => {
    return '#'.repeat(paginator.value.pages.toString().length + 1)
})

const setPaginator = (paginator: Pagination): Pagination => {
    pageInput.value = paginator.currentPage
    // Запись в $route.query
    if (props.useParams) {
        const query = { ...route.query }
        if (paginator.limit) {
            query.limit = `${paginator.limit}`
        }
        if (paginator.currentPage) {
            query.page = `${paginator.currentPage}`
        }

        router.push({ name: route.name || '', params: route.params, hash: route.hash, query })
    }

    emit('update:modelValue', paginator)
    nextTick(() => {
        emit('change', paginator)
    })
    return paginator
}

const onChangeLimit = (limit: PaginationItem): void => {
    const _paginator = {
        ...paginator.value,
        currentPage: 1,
        limit: parseInt(limit.value, 10)
    }
    emit('change-limit', setPaginator(_paginator).limit)
}

const onPaginate = (previousPage: boolean): void => {
    const _paginator = unref(paginator)
    if (previousPage && !isDisabledFirst.value && _paginator.currentPage > 1) {
        _paginator.currentPage -= 1
        setPaginator(_paginator)
    }
    if (!previousPage && !isDisabledLast.value && _paginator.currentPage < _paginator.pages) {
        _paginator.currentPage += 1
        setPaginator(_paginator)
    }
}

const onBorderPaginate = (firstPage: boolean): void => {
  const pager = unref(paginator)
  if (firstPage && !isDisabledFirst.value) {
      pager.currentPage = 1
  }
  if (!firstPage && !isDisabledLast.value) {
      pager.currentPage = paginator.value.pages
  }
  setPaginator(pager)
}

function setPage(value: number) {
  setPaginator({ ...props.modelValue, currentPage: value })
}

const startEdit = (): void => {
    edit.value = true
    nextTick(() => {
      editPageInput.value?.inputControl.select()
    })
}

const stopEdit = (): void => {
    const value = +pageInput.value
    edit.value = false
    page.value = Math.max(Math.min(value, paginator.value.pages), 1)
}

const onEnterDown = () => {
    editPageInput.value?.inputControl.blur()
}

watch(route, () => {
    if (process.client && props.useParams && JSON.stringify(paginator.value) !== JSON.stringify(props.modelValue)) {
      setPaginator(paginator.value)
    }
  },
  { immediate: true }
)

defineExpose({ page })
</script>